import { TableScroll } from '@web/components/table-scroll.tsx';
import { useReportPaging, ReportPager } from '@web/components/report-pagination.tsx';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { EmptyState, Select } from '@web/components/ui.tsx';
import { formatMargin, marginTone } from '@web/components/schedule/job-profit-card.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { formatMinutes } from '@web/i18n/format.ts';
import { getDateLocale } from '@web/i18n/locale.ts';
import type { JobProfitPattern, JobProfitReport, JobProfitRow } from '@shared/types/index.ts';
import {
  reportRetry,
  useReportDate,
  StatTile,
  ReportBoundary,
  SectionCard,
  CustomerLink,
  TH,
  THR,
  TD,
  TDR,
  ROW,
  type RangeParams,
} from '../shared.tsx';

type ServiceType = NonNullable<JobProfitRow['serviceType']>;
const SERVICE_TYPES: readonly ServiceType[] = ['one_time', 'weekly', 'biweekly', 'monthly'];

/** One pattern as a sentence: whose jobs, which service, how far off. */
function patternSentence(pattern: JobProfitPattern, t: ReturnType<typeof useTranslation>['t']): string {
  const service = pattern.serviceType ? t(`reports.jobProfit.service.${pattern.serviceType}`) : null;
  const subject =
    pattern.customerName && service
      ? t('reports.jobProfit.patterns.customerService', { customer: pattern.customerName, service })
      : pattern.customerName
        ? t('reports.jobProfit.patterns.customer', { customer: pattern.customerName })
        : t('reports.jobProfit.patterns.service', { service: service ?? '' });
  if (pattern.overrunBasisPoints !== null && pattern.overrunBasisPoints >= 1500) {
    return t('reports.jobProfit.patterns.overrun', {
      subject,
      percent: Math.round(pattern.overrunBasisPoints / 100),
      count: pattern.visits,
    });
  }
  return t('reports.jobProfit.patterns.marginGap', {
    subject,
    points: Math.round(Math.abs(pattern.marginGapBasisPoints) / 100),
    count: pattern.visits,
  });
}

/**
 * What every closed visit made after labor, card fees and supplies — the
 * owner's view of which jobs, and which customers, are underpriced. Pro.
 */
export function JobProfitSection({ range, scope }: { range: RangeParams; scope?: 'all' }) {
  const formatDate = useReportDate();
  const { t } = useTranslation();
  const [userId, setUserId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [serviceType, setServiceType] = useState('');
  const filters = {
    ...(userId ? { userId } : {}),
    ...(customerId ? { customerId } : {}),
    ...(serviceType ? { serviceType } : {}),
  };
  const paging = useReportPaging({ ...range, scope, ...filters });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'job-profit', { ...range, scope, ...filters, pages }, paging.identity],
    queryFn: () => reportsApi.jobProfit({ ...range, scope, ...filters, pages }),
    retry: reportRetry,
  });
  // The pickers keep the people and customers the unfiltered range had, so
  // choosing one does not shrink the list to itself.
  const [options, setOptions] = useState<{
    crew: JobProfitReport['crewOptions'];
    customers: JobProfitReport['customerOptions'];
  }>({ crew: [], customers: [] });
  const data = query.data;
  if (data && !userId && !customerId && !serviceType && data.crewOptions !== options.crew) {
    setOptions({ crew: data.crewOptions, customers: data.customerOptions });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 [&>div]:mb-0">
        <Select
          label={t('reports.jobProfit.filters.crew')}
          value={userId}
          options={[
            { value: '', label: t('reports.jobProfit.filters.everyone') },
            ...options.crew.map((member) => ({ value: member.userId, label: member.name })),
          ]}
          onChange={(event) => setUserId(event.target.value)}
        />
        <Select
          label={t('reports.jobProfit.filters.customer')}
          value={customerId}
          options={[
            { value: '', label: t('reports.jobProfit.filters.allCustomers') },
            ...options.customers.map((customer) => ({ value: customer.customerId, label: customer.customerName })),
          ]}
          onChange={(event) => setCustomerId(event.target.value)}
        />
        <Select
          label={t('reports.jobProfit.filters.service')}
          value={serviceType}
          options={[
            { value: '', label: t('reports.jobProfit.filters.allServices') },
            ...SERVICE_TYPES.map((value) => ({ value, label: t(`reports.jobProfit.service.${value}`) })),
          ]}
          onChange={(event) => setServiceType(event.target.value)}
        />
      </div>

      <ReportBoundary query={query} paging={paging}>
        {(report) => {
          const money = (cents: number) => formatCents(cents, report.currencyCode, getDateLocale());
          const margin = (basisPoints: number | null) => (basisPoints === null ? '—' : formatMargin(basisPoints));
          const tone = (row: { profitCents: number; marginBasisPoints: number | null }) =>
            marginTone({ ...row, targetMarginBasisPoints: report.targetMarginBasisPoints });
          if (report.rows.length === 0 && (report.pagination?.rows?.page ?? 1) === 1) {
            return (
              <EmptyState title={t('reports.jobProfit.emptyTitle')}>{t('reports.jobProfit.emptyBody')}</EmptyState>
            );
          }
          const excluded =
            report.totals.payMissingVisits + report.totals.timeIncompleteVisits + report.totals.crewMissingVisits;
          return (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <StatTile label={t('reports.jobProfit.totalProfit')} value={money(report.totals.profitCents)} />
                <StatTile
                  label={t('reports.jobProfit.averageMargin')}
                  value={margin(report.totals.averageMarginBasisPoints)}
                  hint={
                    report.targetMarginBasisPoints !== null
                      ? t('reports.jobProfit.target', { target: formatMargin(report.targetMarginBasisPoints) })
                      : t('reports.jobProfit.noTarget')
                  }
                />
                <StatTile
                  label={t('reports.jobProfit.counted')}
                  value={t('reports.jobProfit.countedValue', {
                    counted: report.totals.countedVisits,
                    total: report.totals.visits,
                  })}
                  hint={
                    excluded > 0
                      ? t('reports.jobProfit.excludedHint', {
                          payMissing: report.totals.payMissingVisits,
                          timeIncomplete: report.totals.timeIncompleteVisits,
                          crewMissing: report.totals.crewMissingVisits,
                        })
                      : undefined
                  }
                />
              </div>
              {report.totals.otherCurrencyVisits > 0 ? (
                <p className="field-hint">
                  {t('reports.jobProfit.otherCurrency', { count: report.totals.otherCurrencyVisits })}
                </p>
              ) : null}

              {report.patterns.length > 0 ? (
                <SectionCard title={t('reports.jobProfit.patterns.title')} hint={t('reports.jobProfit.patterns.hint')}>
                  <ul className="list-disc space-y-1 pl-5 text-sm">
                    {report.patterns.map((pattern, index) => (
                      <li key={index}>{patternSentence(pattern, t)}</li>
                    ))}
                  </ul>
                </SectionCard>
              ) : null}

              {report.lowest.length > 0 ? (
                <SectionCard title={t('reports.jobProfit.lowestTitle')} hint={t('reports.jobProfit.lowestHint')}>
                  <TableScroll aria-label={t('reports.jobProfit.lowestTitle')} className="overflow-x-auto">
                    <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                      <thead>
                        <tr className={ROW}>
                          <th scope="col" className={TH}>
                            {t('reports.jobProfit.col.date')}
                          </th>
                          <th scope="col" className={TH}>
                            {t('reports.jobProfit.col.customer')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.jobProfit.col.revenue')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.jobProfit.col.profit')}
                          </th>
                          <th scope="col" className={THR}>
                            {t('reports.jobProfit.col.margin')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.lowest.map((row) => (
                          <tr key={row.appointmentId} className={ROW}>
                            <td className={TD}>
                              <Link
                                to={`/schedule?appointment=${row.appointmentId}`}
                                className="text-[color:var(--color-brand)] hover:underline"
                              >
                                {formatDate(row.scheduledDate)}
                              </Link>
                            </td>
                            <td className={TD}>
                              <CustomerLink id={row.customerId} name={row.customerName} />
                            </td>
                            <td className={TDR}>{money(row.revenueCents)}</td>
                            <td className={TDR}>{money(row.profitCents)}</td>
                            <td className={`${TDR} ${tone(row)}`}>{margin(row.marginBasisPoints)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableScroll>
                </SectionCard>
              ) : null}

              <SectionCard
                title={t('reports.jobProfit.byCustomerTitle')}
                collection="byCustomer"
                hint={t('reports.jobProfit.byCustomerHint')}
              >
                <TableScroll aria-label={t('reports.jobProfit.byCustomerTitle')} className="overflow-x-auto">
                  <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                    <thead>
                      <tr className={ROW}>
                        <th scope="col" className={TH}>
                          {t('reports.jobProfit.col.customer')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.visits')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.revenue')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.profit')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.margin')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.estimatedMargin')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.byCustomer.map((row) => (
                        <tr key={row.customerId} className={ROW}>
                          <td className={TD}>
                            <CustomerLink id={row.customerId} name={row.customerName} />
                            {row.belowTarget ? (
                              <span className="ml-2 badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
                                {t('reports.jobProfit.belowTarget')}
                              </span>
                            ) : null}
                          </td>
                          <td className={TDR}>{row.visits}</td>
                          <td className={TDR}>{money(row.revenueCents)}</td>
                          <td className={TDR}>{money(row.profitCents)}</td>
                          <td className={`${TDR} ${tone(row)}`}>{margin(row.marginBasisPoints)}</td>
                          <td className={TDR}>{margin(row.estimatedMarginBasisPoints)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              </SectionCard>

              <SectionCard
                title={t('reports.jobProfit.jobsTitle')}
                collection="rows"
                hint={report.truncated ? t('reports.jobProfit.truncated') : t('reports.jobProfit.jobsHint')}
                csvHref={reportsApi.csvUrl('job-profit', { ...range, scope, ...filters })}
              >
                <TableScroll aria-label={t('reports.jobProfit.jobsTitle')} className="overflow-x-auto">
                  <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                    <thead>
                      <tr className={ROW}>
                        <th scope="col" className={TH}>
                          {t('reports.jobProfit.col.date')}
                        </th>
                        <th scope="col" className={TH}>
                          {t('reports.jobProfit.col.customer')}
                        </th>
                        <th scope="col" className={TH}>
                          {t('reports.jobProfit.col.crew')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.revenue')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.labor')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.fees')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.supplies')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.profit')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.margin')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.estimatedMargin')}
                        </th>
                        <th scope="col" className={THR}>
                          {t('reports.jobProfit.col.hours')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row) => (
                        <tr key={row.appointmentId} className={ROW}>
                          <td className={TD}>
                            <Link
                              to={`/schedule?appointment=${row.appointmentId}`}
                              className="text-[color:var(--color-brand)] hover:underline"
                            >
                              {formatDate(row.scheduledDate)}
                            </Link>
                            {row.payMissing ? (
                              <span className="ml-2 badge border-[color:var(--color-danger)] text-[color:var(--color-danger)]">
                                {t('reports.jobProfit.payMissing')}
                              </span>
                            ) : row.timeIncomplete ? (
                              <span className="ml-2 badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
                                {t('reports.jobProfit.timeIncomplete')}
                              </span>
                            ) : row.crewMissing ? (
                              <span className="ml-2 badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
                                {t('reports.jobProfit.crewMissing')}
                              </span>
                            ) : null}
                          </td>
                          <td className={TD}>
                            <CustomerLink id={row.customerId} name={row.customerName} />
                          </td>
                          <td className={TD}>{row.crew.map((member) => member.name).join(', ') || '—'}</td>
                          <td className={TDR}>{money(row.revenueCents)}</td>
                          <td className={TDR}>{money(row.laborCents + row.burdenCents)}</td>
                          <td className={TDR}>{money(row.feeCents)}</td>
                          <td className={TDR}>{money(row.suppliesCents)}</td>
                          <td className={TDR}>{row.payMissing || row.crewMissing ? '—' : money(row.profitCents)}</td>
                          <td className={`${TDR} ${row.payMissing || row.crewMissing ? '' : tone(row)}`}>
                            {row.payMissing || row.crewMissing ? '—' : margin(row.marginBasisPoints)}
                          </td>
                          <td className={TDR}>{margin(row.estimatedMarginBasisPoints)}</td>
                          <td className={TDR}>
                            {formatMinutes(row.laborMinutes)}
                            {row.estimatedLaborMinutes !== null ? ` / ${formatMinutes(row.estimatedLaborMinutes)}` : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              </SectionCard>
            </>
          );
        }}
      </ReportBoundary>

      <AccountantExportCard range={range} scope={scope} filters={filters} />
      <ContractorSummaryCard scope={scope} />
      <p className="field-hint">{t('reports.jobProfit.disclaimer')}</p>
    </div>
  );
}

/** The spreadsheets for the accountant, for the same range the report shows. */
function AccountantExportCard({
  range,
  scope,
  filters,
}: {
  range: RangeParams;
  scope?: 'all';
  filters: { userId?: string; customerId?: string; serviceType?: string };
}) {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('reports.jobProfit.export.title')} hint={t('reports.jobProfit.export.hint')}>
      <div className="flex flex-wrap gap-3">
        <a
          href={reportsApi.accountantExportUrl('visits', { ...range, scope, ...filters, format: 'xlsx' })}
          className="btn btn-secondary"
        >
          {t('reports.downloadXlsx')}
        </a>
        <a
          href={reportsApi.accountantExportUrl('visits', { ...range, scope, ...filters })}
          className="btn btn-secondary"
        >
          {t('reports.jobProfit.export.visits')}
        </a>
        <a
          href={reportsApi.accountantExportUrl('workers', { ...range, scope, ...filters })}
          className="btn btn-secondary"
        >
          {t('reports.jobProfit.export.workers')}
        </a>
      </div>
      <p className="field-hint mt-2">
        {t('reports.jobProfit.export.emailHint')}{' '}
        <Link to="/settings#settings-teamPay" className="underline">
          {t('reports.jobProfit.export.emailLink')}
        </Link>
      </p>
    </SectionCard>
  );
}

/** Each 1099 contractor's pay for a calendar year, and whether a W-9 is on file. */
function ContractorSummaryCard({ scope }: { scope?: 'all' }) {
  const formatDate = useReportDate();
  const { t } = useTranslation();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const paging = useReportPaging({ year, scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'contractors', { year, scope, pages }, paging.identity],
    queryFn: () => reportsApi.contractors({ year, scope, pages }),
    retry: reportRetry,
  });
  return (
    <SectionCard
      title={t('reports.jobProfit.contractors.title')}
      hint={t('reports.jobProfit.contractors.hint')}
      csvHref={reportsApi.csvUrl('contractors', { year, scope })}
    >
      <div className="mb-3 max-w-[10rem]">
        <Select
          label={t('reports.jobProfit.contractors.year')}
          value={String(year)}
          options={[thisYear, thisYear - 1, thisYear - 2].map((value) => ({
            value: String(value),
            label: String(value),
          }))}
          onChange={(event) => setYear(Number(event.target.value))}
        />
      </div>
      <ReportBoundary query={query} paging={paging}>
        {(summary) => {
          const money = (cents: number) => formatCents(cents, summary.currencyCode, getDateLocale());
          if (summary.rows.length === 0 && (summary.pagination?.rows?.page ?? 1) === 1) {
            return (
              <p className="text-sm text-[color:var(--color-muted)]">{t('reports.jobProfit.contractors.empty')}</p>
            );
          }
          return (
            <>
              <TableScroll aria-label={t('reports.jobProfit.contractors.title')} className="overflow-x-auto">
                <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                  <thead>
                    <tr className={ROW}>
                      <th scope="col" className={TH}>
                        {t('reports.jobProfit.contractors.name')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.jobProfit.col.visits')}
                      </th>
                      <th scope="col" className={THR}>
                        {t('reports.jobProfit.contractors.paid')}
                      </th>
                      <th scope="col" className={TH}>
                        {t('reports.jobProfit.contractors.w9')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.rows.map((row) => (
                      <tr key={row.userId} className={ROW}>
                        <td className={TD}>{row.name}</td>
                        <td className={TDR}>{row.visits}</td>
                        <td className={TDR}>{money(row.totalPaidCents)}</td>
                        <td className={TD}>
                          {row.w9ReceivedOn
                            ? t('reports.jobProfit.contractors.w9Received', { date: formatDate(row.w9ReceivedOn) })
                            : t('reports.jobProfit.contractors.w9Missing')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              <ReportPager collection="rows" />
              {summary.payMissingVisits > 0 ? (
                <p className="field-hint mt-2">
                  {t('reports.jobProfit.contractors.payMissing', { count: summary.payMissingVisits })}
                </p>
              ) : null}
            </>
          );
        }}
      </ReportBoundary>
    </SectionCard>
  );
}
